import { ref, reactive, computed } from "vue";
import asyncOperations from "@/client/extensions/composition/asyncOperations";

let globalRefreshInterval;
let globalRefreshDelay = 20000;
let finalNetworkQuality = computed(() => {
  if (hasNetworkQuality.value) {
    return currentNetworkQuality;
  }
  return false;
});
let isIntervalSet = false;

let currentNetworkQuality = reactive({
  speed: 0,
  grade: 0,
  label: "",
  up: {
    speed: 0,
    grade: 0,
    label: "",
  },
  down: {
    speed: 0,
    grade: 0,
    label: "",
  },
});
let hasNetworkQuality = ref(false);
let isUpdating = ref(false);
export default (options = {}) => {
  const { asyncOps } = asyncOperations();
  let immediate = false;

  if (utilities.isSSR()) {
    return {
      networkQuality: false,
      isUpdating: false,
      networkQualityChecked: false,
      updateConnectionQuality: () => {
        return false;
      },
    };
  }

  let defaultDownThresholds = [
    {
      speed: 1,
      grade: 0,
      label: "core.networkQuality.veryPoor",
    },
    {
      speed: 0.75,
      grade: 1,
      label: "core.networkQuality.poor",
    },
    {
      speed: 2,
      grade: 2,
      label: "core.networkQuality.good",
    },
    {
      speed: 5,
      grade: 3,
      label: "core.networkQuality.veryGood",
    },
    {
      speed: 10,
      grade: 4,
      label: "core.networkQuality.excellent",
    },
  ];

  let defaultUpThresholds = [
    {
      speed: 1,
      grade: 0,
      label: "core.networkQuality.veryPoor",
    },
    {
      speed: 0.5,
      grade: 1,
      label: "core.networkQuality.poor",
    },
    {
      speed: 1,
      grade: 2,
      label: "core.networkQuality.good",
    },
    {
      speed: 2,
      grade: 3,
      label: "core.networkQuality.veryGood",
    },
    {
      speed: 4,
      grade: 4,
      label: "core.networkQuality.excellent",
    },
  ];

  let downThresholds, upThresholds;

  let implementOptions = () => {
    // normalize
    if (!options || typeof options !== "object") {
      options = {};
    }

    // up thresholds
    upThresholds = defaultUpThresholds;
    if (options.upThresholds) {
      upThresholds = options.upThresholds;
    }
    if (options.upThresholds) {
      upThresholds = options.upThresholds;
    }

    downThresholds = defaultDownThresholds;
    if (options.thresholds) {
      downThresholds = options.thresholds;
    }
    if (options.downThresholds) {
      downThresholds = options.downThresholds;
    }

    if (options.checkInterval) {
      globalRefreshDelay = options.checkInterval;
    }

    if (globalRefreshDelay < 5000) {
      warn(
        "network quality composition - options.checkInterval is less than 5000. Setting 5000",
        options
      );
      globalRefreshDelay = 5000;
    }
    if (options.immediate || options.checkImmediatly) {
      immediate = true;
    }
  };

  let getConnectionSpeed = async () => {
    if (utilities.isSSR()) {
      return false;
    }

    let downTestTimeout, upTestTimeout;

    let getDownConnectionSpeedPromise = () => {
      return new Promise(async (fulfil, reject) => {
        clearTimeout(downTestTimeout);
        let downloadSize = 509000;
        let startTime = new Date().getTime();
        await asyncOps.asyncCall(
          "test/get-payload/",
          { size: downloadSize },
          { cache: { enable: false } }
        );
        let duration = (new Date().getTime() - startTime) / 1000;
        let speedMbps = (downloadSize / duration / (1024 * 1024)).toFixed(2);
        fulfil(speedMbps);

        downTestTimeout = setTimeout(() => {
          fulfil(0.01);
        }, 5000);
      });
    };

    let getUpConnectionSpeedPromise = () => {
      return new Promise(async (fulfil, reject) => {
        clearTimeout(upTestTimeout);
        let uploadSize = 100000;
        let url = `/test`;
        let startTime = new Date().getTime();
        await asyncOps.asyncCall(
          url,
          String("a", { cache: { enable: false } }).repeat(uploadSize)
        );
        let duration = (new Date().getTime() - startTime) / 1000;
        let speedMbps = (uploadSize / duration / (1024 * 1024)).toFixed(2);
        fulfil(speedMbps);

        upTestTimeout = setTimeout(() => {
          fulfil(0.01);
        }, 5000);
      });
    };

    let results = await Promise.allSettled([
      getDownConnectionSpeedPromise(),
      getUpConnectionSpeedPromise(),
    ]);
    return {
      down: results[0].value,
      up: results[1].value,
    };
  };

  let setNetworkQualityBySpeed = (speed, direction = "down") => {
    let thresholds = direction !== "down" ? upThresholds : downThresholds;

    let level = thresholds[0];

    for (const threshold of thresholds) {
      if (speed > threshold.speed) {
        level = threshold;
        continue;
      } else {
        break;
      }
    }
    thresholds.forEach((threshold) => {});

    if (direction === "down") {
      currentNetworkQuality.speed = speed;
      currentNetworkQuality.grade = level.grade;
      currentNetworkQuality.label = level.label;
    }

    currentNetworkQuality[direction] = {};
    currentNetworkQuality[direction].speed = speed;
    currentNetworkQuality[direction].grade = level.grade;
    currentNetworkQuality[direction].label = level.label;
  };

  let updateConnectionQuality = async () => {
    isUpdating.value = true;
    let speeds = await getConnectionSpeed();
    let downSpeed = speeds.down;
    let upSpeed = speeds.up;
    isUpdating.value = false;
    currentNetworkQuality.speed = downSpeed;

    if (downSpeed) {
      setNetworkQualityBySpeed(downSpeed, "down");
      setNetworkQualityBySpeed(upSpeed, "up");
      hasNetworkQuality.value = true;
      return true;
    }

    return false;
  };

  implementOptions();

  if (immediate) {
    updateConnectionQuality();
  }

  clearInterval(globalRefreshInterval);

  // this is SSR safe, because a dummy was returned on SSR and this never runs on ssr
  if (utilities.isSSR()) {
    throw new Error("this must not run on the server");
  }

  let pause = () => {
    clearInterval(globalRefreshInterval);
  };

  let resume = () => {
    clearInterval(globalRefreshInterval);
    globalRefreshInterval = setInterval(() => {
      updateConnectionQuality();
    }, globalRefreshDelay);
  };

  let start = resume;

  // starting immediatly causes issues large unexpected requests. so it is disabled.
  // user must run start
  // start();
  return {
    networkQuality: currentNetworkQuality,
    isUpdating: isUpdating,
    networkQualityChecked: hasNetworkQuality,
    updateConnectionQuality: updateConnectionQuality,
    updateNetworkQuality: updateConnectionQuality,
    pause,
    resume,
    start,
  };
};
